import { queryClient } from "@/App";
import InscricaoDesconto, { EnumTipoValor } from "@/Models/Domain/Viagem/Inscricao/InscricaoDesconto";
import { IPagina, IPaginaInfo, PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import { Axios } from "@/Services/Axios";
import RequestUtilitario, { MutationBasicaParam, QueryBasicaParam } from "@/Utils/RequisicaoUtilitario";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

export function useInscricaoDescontoDesativar({
    onSuccess,
    ...rest
}:MutationBasicaParam<InscricaoDesconto, AxiosResponse<void>>) {
    return useMutation({
        ...rest,
        mutationFn: (movimentacao) => {
            return Axios.delete<void>(`domain/viagem/inscricao/inscricaoDesconto/${movimentacao.codigo}`);
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Domain", "Viagem", "Inscricao", "InscricaoDesconto"] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}
export function useInscricaoDescontoSalvar({
    onSuccess,
    ...rest
}:MutationBasicaParam<InscricaoDesconto, AxiosResponse<void>>) {
    return useMutation({
        ...rest,
        mutationFn: (inscricaoDesconto) => {
            inscricaoDesconto.codigo = typeof inscricaoDesconto.codigo === "number" ? inscricaoDesconto.codigo : Number.parseInt(inscricaoDesconto.codigo as any);
            const isAlteracao = !isNaN(inscricaoDesconto.codigo) && inscricaoDesconto.codigo > 0;
            
            return isAlteracao 
                ? Axios.put<void>(`domain/viagem/inscricao/inscricaoDesconto/${inscricaoDesconto.codigo}`, inscricaoDesconto) 
                : Axios.post<void>(`domain/viagem/inscricao/inscricaoDesconto`, inscricaoDesconto)
            ;
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Domain", "Viagem", "Inscricao", "InscricaoDesconto"] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}

export function useInscricaoDescontoBuscar({
    codigo,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<InscricaoDesconto|null>, InscricaoDesconto|null, {
    codigo:number;
    onFinish?:() => void;
}>) {
    return useQuery({
        ...rest,
        queryKey : [ "Domain", "Viagem", "Inscricao", "InscricaoDesconto", "BuscarPorIdentificador", codigo ],
        queryFn : () => Axios.get<InscricaoDesconto|null>(`domain/viagem/inscricao/inscricaoDesconto/${codigo}`).finally(onFinish),
        select(data) {
            return data.data ?? null;
        },
        staleTime : 1000 * 60 * 0.3
    });
}

export function useInscricaoDescontoListarPagina({
    pagina,
    inscricaoDesconto,
    codTipoValor,
    ativo,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<IPagina<InscricaoDesconto>>, IPagina<InscricaoDesconto>, {
    pagina:IPaginaInfo,
    inscricaoDesconto?:InscricaoDesconto|null,
    codTipoValor?:EnumTipoValor,
    ativo?:boolean|null,
    onFinish?:() => void
}>) {
    const queryString = RequestUtilitario.objectToQueryString({
        pagina : (pagina ?? new PaginaInfo()),
        inscricaoDesconto : (inscricaoDesconto ?? {}),
        codTipoValor,
        ativo,
    });

    return useQuery({
        ...rest,
        queryKey : [ "Domain", "Viagem", "Inscricao", "InscricaoDesconto", "Listar", "Pagina", queryString ],
        queryFn : () => Axios.get<IPagina<InscricaoDesconto>>(`domain/viagem/inscricao/inscricaoDesconto/pagina?${queryString}`).finally(onFinish),
        select(data) {
            const pagina = data.data;

            pagina.registros = pagina.registros.map(m => {
                m.dataCriacao = new Date(m.dataCriacao as any);
                return m;
            });

            return pagina;
        },
        staleTime : 1000 * 60 * 0.3
    });
}

export function useInscricaoDescontoBuscarPorViagemAluno({
    codViagem,
    codPessoaAluno,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<InscricaoDesconto|null>, InscricaoDesconto|null, {
    codViagem:number;
    codPessoaAluno:number;
    onFinish?:() => void;
}>) {
    return useQuery({
        ...rest,
        queryKey : [ "Domain", "Viagem", "Inscricao", "InscricaoDesconto", "PorViagemAluno", codViagem, codPessoaAluno ],
        queryFn : () => Axios.get<InscricaoDesconto|null>(`domain/viagem/${codViagem}/inscricao/inscricaoDesconto/${codPessoaAluno}`).finally(onFinish),
        select(data) {
            return data.data ?? null;
        },
        staleTime : 1000 * 60 * 0.3
    });
}